import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { GlobalVariable } from "../../app.component";
import { DataService, Response } from "../../dataService/data.service";

// Negative-flow data comes from POST /slug/ai-review/bootstrap — the same
// source the consumer app's 4-screen flow uses (keywords.negative +
// word_pools.negative). Nothing here is hardcoded.
interface KeywordItem {
  id: string | null;
  text: string;
  display_order?: number;
}
interface WordPoolItem {
  id: string | null;
  text: string;
  keyword_id?: string | null;
  display_order?: number;
}

const MAX_WORDS = 3;

@Component({
  selector: "app-negative-feedback",
  templateUrl: "./negative-feedback.component.html",
  styleUrls: ["./negative-feedback.component.scss"],
})
export class NegativeFeedbackComponent implements OnInit {
  private BASE_URL = GlobalVariable;

  @Input() businessName = "";
  // Review slug minted upstream (details form / anonymous) — all the
  // ai-review endpoints are slug-based.
  @Input() slug = "";
  // Word-intent chips ("Pick the details") are a v2-UI extra — hidden in the
  // default variant.
  @Input() showWordIntents = false;
  @Output() submitted = new EventEmitter<{
    keywords: string[];
    words: string[];
    feedbackText: string;
  }>();
  // Bootstrap says feedback already exists for this slug — parent shows the
  // thank-you screen with an "already received" note.
  @Output() alreadyGiven = new EventEmitter<void>();
  // Customer wants to go back to the thumbs choice (e.g. tapped 👎 by mistake).
  @Output() back = new EventEmitter<void>();

  // Bootstrap state. On failure the slug is kept upstream — retry re-fetches.
  bootLoading = true;
  bootError = false;

  keywords: KeywordItem[] = [];
  maxWords = MAX_WORDS;

  // Full negative word pool from bootstrap (id lookups for capture).
  private bootstrapPool: WordPoolItem[] = [];

  // Current word-intent options (texts from the bootstrap pool).
  wordOptions: string[] = [];

  selectedKeywords: KeywordItem[] = [];
  selectedWords: string[] = [];
  feedbackText = "";

  submitting = false;

  constructor(private http: DataService, private toastr: ToastrService) {}

  ngOnInit(): void {
    this.fetchBootstrap();
  }

  // ── Bootstrap ─────────────────────────────────────────────────────
  fetchBootstrap(): void {
    this.bootLoading = true;
    this.bootError = false;
    this.http
      .postjson(this.BASE_URL.AI_REVIEW_BOOTSTRAP, { slug: this.slug })
      .subscribe(
        (res: Response) => {
          if (res.code === 200) {
            const data =
              typeof res.data === "string" ? JSON.parse(res.data) : res.data;
            if (data.slug_has_feedback) {
              this.bootLoading = false;
              this.alreadyGiven.emit();
              return;
            }
            // The Others sentinel may arrive inside keywords.negative (db path);
            // drop it — the Others option was removed from the negative flow.
            const othersSentinel = (data.others_keyword || "Others").toLowerCase();
            this.keywords = (data.keywords?.negative || []).filter(
              (k: KeywordItem) => k.text.toLowerCase() !== othersSentinel
            );
            this.bootstrapPool = data.word_pools?.negative || [];
            this.bootError = false;
          } else {
            this.bootError = true;
          }
          this.bootLoading = false;
        },
        () => {
          this.bootError = true;
          this.bootLoading = false;
        }
      );
  }

  // ── Keyword selection ─────────────────────────────────────────────
  isKeywordSelected(kw: KeywordItem): boolean {
    return this.selectedKeywords.some((k) => k.text === kw.text);
  }

  toggleKeyword(kw: KeywordItem): void {
    const i = this.selectedKeywords.findIndex((k) => k.text === kw.text);
    if (i >= 0) this.selectedKeywords.splice(i, 1);
    else this.selectedKeywords.push(kw);
    this.refreshWordPool();
  }

  // ── Word-intent pool ──────────────────────────────────────────────
  // Derived locally from the bootstrap word_pools.negative — intents tied to
  // a selected keyword (keyword_id match) plus the sentiment-global ones
  // (keyword_id null). No word-pool API call.
  private refreshWordPool(): void {
    if (!this.selectedKeywords.length) {
      this.wordOptions = [];
      this.selectedWords = [];
      return;
    }
    const ids = this.selectedKeywords.map((k) => k.id).filter(Boolean);
    const pool = this.bootstrapPool
      .filter((w) => !w.keyword_id || ids.includes(w.keyword_id))
      .map((w) => w.text);
    // Dedupe while keeping order.
    this.wordOptions = pool.filter((w, i) => pool.indexOf(w) === i);
    this.selectedWords = this.selectedWords.filter((w) =>
      this.wordOptions.includes(w)
    );
  }

  isWordSelected(word: string): boolean {
    return this.selectedWords.includes(word);
  }

  toggleWord(word: string): void {
    const i = this.selectedWords.indexOf(word);
    if (i >= 0) {
      this.selectedWords.splice(i, 1);
    } else if (this.selectedWords.length < this.maxWords) {
      this.selectedWords.push(word);
    }
  }

  // ── Submit ────────────────────────────────────────────────────────
  // Chips and text are all optional — a bare thumbs-down still counts as
  // completed feedback.
  submit(): void {
    if (this.submitting) return;

    const keywordTexts = this.selectedKeywords.map((k) => k.text);
    const freeText = this.feedbackText.trim();

    // Dashboard-facing feedback text: topics (details) — free text.
    const parts: string[] = [];
    if (keywordTexts.length) {
      const detail = this.selectedWords.length
        ? ` (${this.selectedWords.join(", ")})`
        : "";
      parts.push(`${keywordTexts.join(", ")}${detail}`);
    }
    if (freeText) parts.push(freeText);
    const feedbackText = parts.join(" — ");

    const emitPayload = {
      keywords: keywordTexts,
      words: this.selectedWords.slice(),
      feedbackText: freeText,
    };

    this.submitting = true;

    // Same trio the consumer app's negative flow fires: legacy rating +
    // feedback text + ai-review capture. 202 on feedback = duplicate,
    // still a success for the customer.
    // This app exposes the PUT as putRequest({customApiUrl, data}); the review
    // app's putjson(url, body) has no equivalent here.
    const rating$ = this.http
      .putRequest({
        customApiUrl: this.BASE_URL.REVIEW_RATING_API,
        data: {
          slug: this.slug,
          thumbs: "thumbs_down",
          faces: null,
          formal_rating: null,
          review_scale_type: "TWO_POINT_SCALE",
        },
      })
      .pipe(catchError(() => of(null)));

    const feedback$ = this.http
      .postjson(this.BASE_URL.SMS_SLUG_REQUEST_URL, {
        slug: this.slug,
        feedback_text: feedbackText,
        feedback_star: 1,
      })
      .pipe(catchError(() => of(null)));

    const capture$ = this.http
      .postjson(this.BASE_URL.AI_REVIEW_CAPTURE, {
        slug: this.slug,
        sentiment: "negative",
        selected_keyword_ids: this.selectedKeywords
          .map((k) => k.id)
          .filter(Boolean),
        selected_word_ids: this.bootstrapPool
          .filter((w) => this.selectedWords.includes(w.text) && w.id)
          .map((w) => w.id),
        others_text: "",
        feedback_text: feedbackText,
        posted_to_google: false,
      })
      .pipe(catchError(() => of(null)));

    forkJoin([rating$, feedback$, capture$]).subscribe(
      ([, feedbackRes]) => {
        this.submitting = false;
        const res = feedbackRes as Response | null;
        if (!res) {
          this.toastr.error("Could not send your feedback. Please try again.");
          return;
        }
        if (res.code === 202) {
          // Duplicate — feedback already existed for this slug.
          this.alreadyGiven.emit();
          return;
        }
        this.submitted.emit(emitPayload);
      },
      () => {
        this.submitting = false;
        this.toastr.error("Could not send your feedback. Please try again.");
      }
    );
  }
}
